export const INVOICE_TYPE_OPTIONS = [
	{ value: "", label: "All" },
	{ value: "consultation", label: "Consultation" },
	{ value: "pharmacy", label: "Pharmacy" },
	{ value: "lab", label: "Lab" },
	{ value: "other_services", label: "Other Services" },
];

export const PAYMENT_METHOD_OPTIONS = [
	{ value: "cash", label: "Cash" },
	{ value: "online", label: "Online" },
	// { value: "card", label: "Card" },
	// { value: "upi", label: "UPI" },
	// { value: "bank_transfer", label: "Bank Transfer" },
	// { value: "wallet", label: "Wallet" },
];

export const formatDateDDMMYYYY = (dateString) => {
	if (!dateString) return "N/A";
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return "N/A";
	const day = date.getDate().toString().padStart(2, "0");
	const month = (date.getMonth() + 1).toString().padStart(2, "0");
	const year = date.getFullYear();
	return `${day}/${month}/${year}`;
};

export const formatDateList = (dateString) => {
	if (!dateString) return "N/A";
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return "N/A";
	const day = date.getDate().toString().padStart(2, "0");
	const month = date.toLocaleString(undefined, { month: "short" });
	const year = date.getFullYear();
	return `${day} ${month} ${year}`;
};

export const formatDateTime = (dateString) => {
	if (!dateString) return "N/A";
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return "N/A";
	const day = date.getDate().toString().padStart(2, "0");
	const month = date.toLocaleString(undefined, { month: "short" });
	const year = date.getFullYear();
	const hours = date.getHours().toString().padStart(2, "0");
	const minutes = date.getMinutes().toString().padStart(2, "0");
	return `${day} ${month} ${year}, ${hours}:${minutes}`;
};

export const formatInvoiceType = (type) => {
	if (!type) return "-";
	const map = {
		consultation: "Consultation",
		pharmacy: "Pharmacy",
		lab: "Lab",
		other_services: "Other Services",
		treatment_package: "Treatment Package",
	};
	return map[type] || type.replace(/_/g, " ");
};

export const normalizePaymentStatus = (status) => {
	if (!status) return "unpaid";
	const normalized = String(status).toLowerCase();
	if (normalized === "paid") return "paid";
	if (["partial_paid", "partially_paid", "partial"].includes(normalized)) return "partial_paid";
	return "unpaid";
};

const isAppointmentInvoiceSettled = (appointmentInvoice = {}) =>
	appointmentInvoice?.is_settled === true;

export const isPatientAccountSettled = (record) => {
	if (!record) return false;
	return isAppointmentInvoiceSettled(record.appointment_invoice || {});
};

export const formatAmount = (amount, currency, getCurrencySymbol) => {
	const value = Number(amount || 0).toFixed(2);
	if (getCurrencySymbol && currency) {
		return `${getCurrencySymbol(currency)} ${value}`;
	}
	return currency ? `${value} ${currency}` : value;
};

export const toInputDateValue = (dateString) => {
	if (!dateString) return "";
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return "";
	return date.toISOString().split("T")[0];
};

export const getTodayDateString = () => {
	const today = new Date();
	const year = today.getFullYear();
	const month = String(today.getMonth() + 1).padStart(2, "0");
	const day = String(today.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const toNumber = (value, fallback = 0) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
};

export const getPatientDisplayName = (patient) => {
	if (!patient) return "-";
	if (patient.name) return patient.name;
	const parts = [patient.first_name, patient.middle_name, patient.last_name]
		.filter(Boolean)
		.map((part) => String(part).trim())
		.filter(Boolean);
	return parts.length > 0 ? parts.join(" ") : patient.full_name || "-";
};

export const hasBillingPurchase = (record) => {
	const billing = record?.billing;
	if (!billing || typeof billing !== "object") return false;
	return Object.keys(billing).length > 0;
};

/** Stamp sale date on pharmacy_items; used by pay-remaining / collect payment payloads. */
export const withPharmacyItemSaleDate = (items = [], saleDate = getTodayDateString()) =>
	(items || []).map((item) => ({
		...item,
		date: item.date || item.sale_date || saleDate,
	}));

export const isExistingPharmaSaleRecord = (record) => {
	if (!record) return false;
	if (record.salesId || record.sale_id || record.sales?._id) return true;
	return hasBillingPurchase(record);
};

/** First Collect Payment: paid amount is still 0 (or only unpaid history rows). */
export const isFirstPayRemainingPayment = (record) => {
	if (!record) return true;

	const paidAmount = toNumber(
		record?.appointment_invoice?.paid_amount ??
			record?.package_payment_details?.payment?.paid_amount ??
			record?.billing?.payment?.paid_amount ??
			record?.paid_amount ??
			0
	);
	if (paidAmount > 0) return false;

	const history =
		record?.package_payment_details?.payment_history ||
		record?.appointment_invoice?.payment_history ||
		record?.billing?.payment?.payment_history ||
		record?.payment_history ||
		[];

	return !history.some(
		(item) => toNumber(item?.paid_amount) > 0 || Boolean(item?.receipt_number)
	);
};

/** Pharmacy line items only on the first payment; subsequent pays skip them. */
export const shouldIncludePharmacyInPayRemainingBilling = (record) =>
	isFirstPayRemainingPayment(record);

const stripPharmacyFromBillingSummary = (summary = {}) => {
	if (!summary || typeof summary !== "object") return summary;
	return {
		...summary,
		pharmacy_subtotal: 0,
		pharmacy_discount: 0,
		pharmacy_net_taxable: 0,
		pharmacy_tax: 0,
		pharmacy_CGST: 0,
		pharmacy_SGST: 0,
		pharmacy_IGST: 0,
		pharmacy_VAT: 0,
		pharmacy_total: 0,
		pharmacy_taxable_total: 0,
		pharmacy_line_total: 0,
		pharmacy_net_total: 0,
	};
};

export const requiresPayRemainingBillingPayload = () => true;

export const getPatientAccountPatientId = (record) =>
	record?.patient_id ||
	record?.patient?._id ||
	record?.patient?.id ||
	record?.appointment_invoice?.patient_id ||
	"";

export const hasNonEmptyPayRemainingBillingSection = (billing) => {
	if (!billing || typeof billing !== "object") return false;
	const keys = [
		"consultation",
		"investigations",
		"general_services",
		"pharmacy_items",
		"treatment_package",
	];
	return keys.some((key) => Array.isArray(billing[key]) && billing[key].length > 0);
};

export const validatePayRemainingNewSalePayload = ({
	appointmentId,
	paidAmount,
	paymentType,
	patientId,
	patientName,
	billing,
	t,
}) => {
	const translate = (message) => (typeof t === "function" ? t(message) : message);
	const errors = {};

	if (!appointmentId) {
		errors.appointmentId = translate("Appointment not found for this invoice.");
	}
	if (!paymentType) {
		errors.paymentType = translate("Payment method is required.");
	}
	if (!paidAmount || toNumber(paidAmount) <= 0) {
		errors.paidAmount = translate("Amount must be greater than zero.");
	}
	if (!patientId) {
		errors.patientId = translate("Patient is required.");
	}
	if (!String(patientName || "").trim()) {
		errors.patientName = translate("Patient name is required.");
	}
	if (!billing) {
		errors.billing = translate("Billing details are required.");
	} else if (!hasNonEmptyPayRemainingBillingSection(billing)) {
		errors.billing = translate("At least one billing section is required.");
	}

	return errors;
};

export const formatBillingForPayRemainingSales = (billingPayload, record, options = {}) => {
	if (!billingPayload || !record) return null;

	const appointmentId = getCollectPaymentAppointmentId(record);
	const normalized = normalizePatientAccountItem(record);
	const appointmentInvoice = record?.appointment_invoice || {};
	const isTreatment =
		billingPayload.is_treatment_package === true || isTreatmentPackageInvoice(record);
	const currency =
		options.currency ||
		billingPayload.summary?.currency ||
		normalized?.currency ||
		appointmentInvoice.currency ||
		"INR";
	const paidSoFar = toNumber(normalized?.paid_amount);
	const grandTotal = toNumber(
		billingPayload.summary?.grand_total ?? normalized?.invoice_amount ?? appointmentInvoice.net_total
	);
	const pendingAmount = toNumber(
		normalized?.due_amount ?? Math.max(0, grandTotal - paidSoFar)
	);
	const includePharmacy = shouldIncludePharmacyInPayRemainingBilling(record);
	const pharmacyItems = includePharmacy
		? withPharmacyItemSaleDate(billingPayload.pharmacy_items || [])
		: [];
	const summary = includePharmacy
		? billingPayload.summary
		: stripPharmacyFromBillingSummary(billingPayload.summary);

	// Treatment package: keep POS-shaped billing (full invoice + treatment extras + current payment)
	if (isTreatment) {
		return {
			...billingPayload,
			appointment_id: billingPayload.appointment_id || appointmentId,
			patient_id: billingPayload.patient_id || getPatientAccountPatientId(record),
			doctor_id:
				billingPayload.doctor_id ||
				appointmentInvoice.doctor_id ||
				record?.doctor?._id ||
				null,
			appointment_invoice:
				billingPayload.appointment_invoice || appointmentInvoice || {
					appointment_id: appointmentId,
					invoice_number: appointmentInvoice.invoice_number || normalized?.invoice_no,
					invoice_id: appointmentInvoice._id,
					status: appointmentInvoice.status || normalized?.status || "unpaid",
				},
			consultation: billingPayload.consultation || [],
			investigations: billingPayload.investigations || [],
			general_services: billingPayload.general_services || [],
			pharmacy_items: pharmacyItems,
			treatment_package: billingPayload.treatment_package || [],
			summary: {
				...summary,
				grand_total: grandTotal,
				currency: summary?.currency || currency,
			},
			payment: {
				...(billingPayload.payment || {}),
				currency: billingPayload.payment?.currency || currency,
				pending_amount: toNumber(
					billingPayload.payment?.pending_amount ?? pendingAmount
				),
				due_amount: toNumber(
					billingPayload.payment?.due_amount ??
						billingPayload.payment?.pending_amount ??
						pendingAmount
				),
			},
		};
	}

	return {
		...billingPayload,
		appointment_id: billingPayload.appointment_id || appointmentId,
		patient_id: billingPayload.patient_id || getPatientAccountPatientId(record),
		doctor_id:
			billingPayload.doctor_id ||
			appointmentInvoice.doctor_id ||
			record?.doctor?._id ||
			null,
		appointment_invoice: {
			appointment_id: appointmentId,
			invoice_number: appointmentInvoice.invoice_number || normalized?.invoice_no,
			invoice_id: appointmentInvoice._id,
			status: appointmentInvoice.status || normalized?.status || "unpaid",
		},
		consultation: billingPayload.consultation || [],
		investigations: billingPayload.investigations || [],
		general_services: billingPayload.general_services || [],
		pharmacy_items: pharmacyItems,
		summary: {
			...summary,
			grand_total: grandTotal,
			currency,
		},
		payment: {
			paid_amount: paidSoFar,
			pending_amount: pendingAmount,
			due_amount: pendingAmount,
			currency,
		},
	};
};

const isDueAmountConsistent = (invoiceAmount, paidAmount, dueAmount) => {
	if (dueAmount <= 0) return true;
	return Math.abs(paidAmount + dueAmount - invoiceAmount) < 1;
};

const resolveBaseCurrency = (appointmentInvoice, billing) =>
	appointmentInvoice?.currency ||
	billing?.summary?.currency ||
	billing?.payment?.base_currency ||
	"INR";

export const getForeignPaymentSummary = (record, baseCurrency) => {
	const history = record?.appointment_invoice?.payment_history || [];
	const totals = {};

	history.forEach((item) => {
		const collected = item.collected_currency;
		if (!collected || collected === baseCurrency) return;
		const amount = toNumber(item.paid_amount);
		if (amount <= 0) return;
		totals[collected] = toNumber((totals[collected] || 0) + amount);
	});

	return Object.entries(totals).map(([currency, amount]) => ({
		currency,
		amount,
	}));
};

export const formatForeignPaymentSummary = (foreignPayments, getCurrencySymbol) => {
	if (!foreignPayments?.length) return "";
	return foreignPayments
		.map(({ currency, amount }) => formatAmount(amount, currency, getCurrencySymbol))
		.join(" + ");
};

const resolvePatientAccountAmounts = (record, appointmentInvoice, billing) => {
	const isSettled = isAppointmentInvoiceSettled(appointmentInvoice);
	const baseCurrency = resolveBaseCurrency(appointmentInvoice, billing);
	const paymentStatus = normalizePaymentStatus(
		appointmentInvoice.status || billing?.payment?.status || record?.status
	);

	// Prefer pharmacy billing grand total / billable amount — net_total can still be
	// pre-discount invoice subtotal while paid equals the discounted amount.
	const invoiceAmount = toNumber(
		billing?.summary?.grand_total ??
			billing?.payment?.grand_total ??
			appointmentInvoice.billable_amount ??
			appointmentInvoice.net_total ??
			record.invoice_amount
	);

	const paidAmount = toNumber(
		billing?.payment?.paid_amount ??
			appointmentInvoice.paid_amount ??
			record.paid_amount
	);

	if (isSettled || paymentStatus === "paid") {
		return {
			invoice_amount: invoiceAmount,
			paid_amount: paidAmount,
			due_amount: 0,
			pending_amount: 0,
			base_currency: baseCurrency,
		};
	}

	const billingPending = billing?.payment?.pending_amount;
	const computedDue = Math.max(0, invoiceAmount - paidAmount);
	const apiDue = toNumber(
		billingPending ??
			appointmentInvoice.due_amount ??
			record.due_amount ??
			record.pending_amount
	);

	let dueAmount;
	if (billingPending != null) {
		dueAmount = Math.max(0, toNumber(billingPending));
	} else if (paidAmount + 0.01 >= invoiceAmount) {
		dueAmount = 0;
	} else if (apiDue > 0 && isDueAmountConsistent(invoiceAmount, paidAmount, apiDue)) {
		dueAmount = apiDue;
	} else {
		dueAmount = computedDue;
	}

	return {
		invoice_amount: invoiceAmount,
		paid_amount: paidAmount,
		due_amount: dueAmount,
		pending_amount: dueAmount,
		base_currency: baseCurrency,
	};
};

export const normalizePatientAccountItem = (record) => {
	if (!record) return null;

	const appointmentInvoice = record.appointment_invoice || {};
	const billing = hasBillingPurchase(record) ? record.billing : null;
	const amounts = resolvePatientAccountAmounts(record, appointmentInvoice, billing);
	const baseCurrency = amounts.base_currency || resolveBaseCurrency(appointmentInvoice, billing);
	const foreignPayments = getForeignPaymentSummary(record, baseCurrency);

	return {
		...record,
		_recordId: record._id,
		invoiceId: appointmentInvoice._id || record._id,
		invoice_no: appointmentInvoice.invoice_number || record.invoice_no || "",
		invoice_number: appointmentInvoice.invoice_number || record.invoice_no || "",
		invoice_date: appointmentInvoice.createdAt || record.createdAt || record.invoice_date,
		currency: baseCurrency,
		base_currency: baseCurrency,
		is_conversion: appointmentInvoice.is_conversion === true || foreignPayments.length > 0,
		foreign_payments: foreignPayments,
		status:
			appointmentInvoice.status ||
			billing?.payment?.status ||
			record.status,
		invoice_amount: amounts.invoice_amount,
		paid_amount: amounts.paid_amount,
		due_amount: amounts.due_amount,
		pending_amount: amounts.pending_amount,
		is_settled: appointmentInvoice.is_settled === true,
		payment_history:
			appointmentInvoice.payment_history ||
			billing?.payment?.payment_history ||
			record.payment_history ||
			[],
	};
};

export const getCollectPaymentAppointmentId = (record) =>
	record?.appointment_invoice?.appointment_id ||
	record?.billing?.appointment_id ||
	record?.appointment_id ||
	"";

export const buildPayRemainingSalesPayload = ({
	appointmentId,
	paidAmount,
	paymentType = "cash",
	currencyType = "INR",
	isSettled = false,
	isConversion = false,
	invoiceRecord,
	billing,
	patientId,
	patientName,
	items,
	netTotal,
	doctorId,
}) => {
	const payload = {
		appointment_id: appointmentId,
		paidAmount: toNumber(paidAmount),
		paymentType,
		currencyType,
		isSettled: Boolean(isSettled),
		isConversion: Boolean(isConversion),
		billing,
		patientId: patientId || getPatientAccountPatientId(invoiceRecord),
		patientName: patientName || getPatientDisplayName(invoiceRecord?.patient),
		customerType: "hospital_patient",
		isHospitalConnected: true,
	};

	const resolvedDoctorId =
		doctorId ||
		billing?.doctor_id ||
		invoiceRecord?.appointment_invoice?.doctor_id ||
		invoiceRecord?.doctor?._id;

	if (resolvedDoctorId) {
		payload.doctorId = resolvedDoctorId;
	}

	if (Array.isArray(items) && items.length > 0) {
		payload.items = items;
		if (netTotal != null) {
			payload.netTotal = toNumber(netTotal);
		}
	}

	return payload;
};

export const applyPayRemainingSalesResponse = (record, apiData) => {
	if (!record || !apiData?.sales) return record;

	const sales = apiData.sales;
	const salesBilling = sales.billing || {};
	const payment = salesBilling.payment || {};
	const appointmentInvoice = record.appointment_invoice || {};

	const paidAmount = toNumber(
		payment.paid_amount ?? sales.totalPaid ?? appointmentInvoice.paid_amount
	);
	const dueAmount = toNumber(
		payment.pending_amount ??
			payment.due_amount ??
			sales.dueAmount ??
			appointmentInvoice.due_amount
	);
	const isSettled =
		sales.isSettled === true || appointmentInvoice.is_settled === true;
	const paymentStatus =
		payment.status || sales.paymentStatus || appointmentInvoice.status;

	const updatedBilling =
		Object.keys(salesBilling).length > 0
			? { ...(record.billing || {}), ...salesBilling }
			: record.billing;

	return {
		...record,
		salesId: sales._id || record.salesId,
		billing: updatedBilling,
		appointment_invoice: {
			...appointmentInvoice,
			paid_amount: paidAmount,
			due_amount: isSettled ? 0 : dueAmount,
			status: paymentStatus,
			is_settled: isSettled,
			payment_history:
				payment.payment_history || appointmentInvoice.payment_history,
			invoice: sales.invoiceUrl || appointmentInvoice.invoice,
			receipt: sales.receiptUrl || appointmentInvoice.receipt,
			...(salesBilling.invoice_id ? { invoice_id: salesBilling.invoice_id } : {}),
		},
	};
};

export const getPatientAccountRecordStorageKey = (invoiceId) =>
	`patient-account-record-${invoiceId}`;

export const savePatientAccountRecord = (invoiceId, record) => {
	if (!invoiceId || !record) return;
	try {
		sessionStorage.setItem(
			getPatientAccountRecordStorageKey(invoiceId),
			JSON.stringify(record)
		);
	} catch (error) {
		console.error("Failed to cache patient account record:", error);
	}
};

export const loadPatientAccountRecord = (invoiceId, locationState) => {
	if (locationState?.record) {
		return locationState.record;
	}

	if (!invoiceId) return null;

	try {
		const cached = sessionStorage.getItem(getPatientAccountRecordStorageKey(invoiceId));
		return cached ? JSON.parse(cached) : null;
	} catch (error) {
		console.error("Failed to read cached patient account record:", error);
		return null;
	}
};

export const getPatientAccountInvoiceId = (record) =>
	record?.appointment_invoice?._id || record?.invoiceId || record?._id || "";

export const buildPatientAccountDetailLines = (record) => {
	if (!record) return [];

	const billing = hasBillingPurchase(record) ? record.billing : null;

	if (billing) {
		const lines = [];

		(billing.consultation || []).forEach((item, index) => {
			const quantity = Math.max(1, Number(item.quantity) || 1);
			const unitPrice = toNumber(item.price ?? item.amount);
			lines.push({
				id: `consultation-${index}`,
				service_name: item.consultation_name || "Consultation",
				service_type: "consultation",
				quantity,
				charges: unitPrice,
				amount: toNumber(item.net_total ?? item.sub_total ?? item.amount ?? unitPrice * quantity),
			});
		});

		(billing.general_services || []).forEach((item, index) => {
			const quantity = Math.max(1, Number(item.quantity) || 1);
			const unitPrice = toNumber(item.price ?? item.amount / quantity);
			lines.push({
				id: item.service_id || `service-${index}`,
				service_name: item.service_name || "Service",
				service_type: "general",
				quantity,
				charges: unitPrice,
				amount: toNumber(item.net_total ?? item.sub_total ?? item.amount ?? unitPrice * quantity),
			});
		});

		(billing.investigations || []).forEach((item, index) => {
			const quantity = Math.max(1, Number(item.quantity) || 1);
			const unitPrice = toNumber(item.price ?? item.amount);
			lines.push({
				id: item.test_id || `investigation-${index}`,
				service_name: item.test_name || "Investigation",
				service_type: item.type || "laboratory",
				quantity,
				charges: unitPrice,
				amount: toNumber(item.net_total ?? item.sub_total ?? item.amount ?? unitPrice * quantity),
			});
		});

		(billing.pharmacy_items || []).forEach((item, index) => {
			const quantity = Math.max(1, Number(item.qty ?? item.quantity) || 1);
			const unitPrice = toNumber(item.unit_price ?? item.price);
			lines.push({
				id: item.item_id || `pharmacy-${index}`,
				service_name: item.item || item.brand_name || "Pharmacy Item",
				service_type: "pharmacy",
				quantity,
				charges: unitPrice,
				amount: toNumber(item.net_total ?? item.amount ?? item.sub_total ?? unitPrice * quantity),
			});
		});

		return lines;
	}

	return (record?.appointment_invoice?.services || []).map((item, index) => {
		const quantity = Math.max(1, Number(item.quantity) || 1);
		const price = toNumber(item.price);
		return {
			id: item._id || `invoice-service-${index}`,
			service_name: item.service_name || item.name || "-",
			service_type: item.service_type || "general",
			quantity,
			charges: price,
			amount: toNumber(item.amount ?? price * quantity),
		};
	});
};

export const buildPatientAccountFinancialSummary = (record) => {
	const normalized = normalizePatientAccountItem(record);
	const appointmentInvoice = record?.appointment_invoice || {};
	const billingSummary = hasBillingPurchase(record) ? record.billing?.summary || {} : {};
	const baseCurrency = normalized?.base_currency || normalized?.currency || "INR";
	const invoiceTotal = toNumber(normalized?.invoice_amount);
	const hasBillingSummary = Object.keys(billingSummary).length > 0;
	const billingGrandTotal = toNumber(billingSummary.grand_total);
	// Use billing section totals whenever pharmacy billing summary exists.
	// Previously required grand_total ≈ net_total, which fails when discount
	// makes billing grand_total (e.g. 1365) differ from invoice net_total (1415).
	const useBillingSections = hasBillingSummary && billingGrandTotal > 0;

	const lines = buildPatientAccountDetailLines(record);
	const lineTotalByTypes = (types) =>
		lines
			.filter((line) => types.includes(line.service_type))
			.reduce((sum, line) => sum + toNumber(line.amount), 0);

	const sectionDiscount = toNumber(
		(billingSummary.services_discount || 0) +
			(billingSummary.investigation_discount || 0) +
			(billingSummary.pharmacy_discount || 0)
	);

	const servicesTotal = useBillingSections
		? toNumber(billingSummary.services_total)
		: lineTotalByTypes(["consultation", "general"]) ||
			toNumber(appointmentInvoice.consultation_total_amount);
	const investigationTotal = useBillingSections
		? toNumber(billingSummary.investigation_total)
		: lineTotalByTypes(["laboratory", "lab"]) ||
			toNumber(appointmentInvoice.laboratory_total_amount);
	const pharmacyTotal = useBillingSections
		? toNumber(billingSummary.pharmacy_total)
		: lineTotalByTypes(["pharmacy"]) || toNumber(appointmentInvoice.pharmacy_total_amount);

	// Match table Amount column (net line totals) — not mixed pre/post discount billing.subtotal
	const linesSubtotal = lines.reduce((sum, line) => sum + toNumber(line.amount), 0);
	const sectionsSubtotal = toNumber(servicesTotal + investigationTotal + pharmacyTotal);

	return {
		currency: baseCurrency,
		sub_total: toNumber(
			useBillingSections
				? linesSubtotal || sectionsSubtotal || billingSummary.grand_total
				: appointmentInvoice.sub_total ?? billingSummary.subtotal ?? invoiceTotal
		),
		discount: toNumber(
			billingSummary.discount > 0 ? billingSummary.discount : sectionDiscount || appointmentInvoice.discount
		),
		billable_amount: toNumber(
			billingSummary.grand_total ??
				appointmentInvoice.billable_amount ??
				appointmentInvoice.net_total ??
				invoiceTotal
		),
		net_total: invoiceTotal,
		total_paid: toNumber(normalized?.paid_amount),
		due: toNumber(normalized?.due_amount ?? normalized?.pending_amount),
		services_total: servicesTotal,
		investigation_total: investigationTotal,
		pharmacy_total: pharmacyTotal,
		tax: toNumber(
			billingSummary.tax ??
				((billingSummary.services_tax || 0) +
					(billingSummary.investigation_tax || 0) +
					(billingSummary.pharmacy_tax || 0) ||
					appointmentInvoice.tax_amount)
		),
		foreign_payments: normalized?.foreign_payments || [],
	};
};

export const isTreatmentPackageInvoice = (record) =>
	record?.is_treatment_package === true ||
	record?.appointment_invoice?.invoice_type === "treatment_package" ||
	Boolean(record?.package_payment_details?.treatment_package);

export const formatGender = (gender) => {
	if (!gender) return "-";
	const normalized = String(gender).toLowerCase();
	if (normalized === "male") return "Male";
	if (normalized === "female") return "Female";
	return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const formatTreatmentSessionSchedule = (session, sessionDetail) => {
	const appointment = sessionDetail?.session?.appointment;
	if (appointment?.appointment_date && appointment?.start_time && appointment?.end_time) {
		return `${formatDateList(appointment.appointment_date)} ${appointment.start_time} - ${appointment.end_time}`;
	}
	if (session?.schedule) return session.schedule;
	if (session?.session_date_time) return formatDateList(session.session_date_time);
	return "-";
};

export const buildTreatmentPackageSessionRows = (record) => {
	const pkgDetails = record?.package_payment_details || {};
	const sessions = pkgDetails.treatment_package?.sessions || [];
	const sessionDetails = pkgDetails.session_details || [];
	const detailsBySessionId = {};

	sessionDetails.forEach((detail) => {
		const sessionId = detail?.session?._id;
		if (sessionId) detailsBySessionId[sessionId] = detail;
	});

	return sessions.map((session, index) => ({
		id: session._id || `session-${index}`,
		index: index + 1,
		sessionName: session.session_name || "-",
		description: session.description || session.session_name || "-",
		duration: session.duration || "-",
		schedule: formatTreatmentSessionSchedule(session, detailsBySessionId[session._id]),
		status: String(session.status || "pending").toLowerCase(),
	}));
};

export const buildTreatmentPackageDetails = (record) => {
	const pkgDetails = record?.package_payment_details || {};
	const treatmentPackage = pkgDetails.treatment_package || {};
	const appointmentInvoice = record?.appointment_invoice || {};
	const payment = pkgDetails.payment || appointmentInvoice;
	const consultation = record?.consultation || {};
	const firstSession = treatmentPackage.sessions?.[0];
	const sessionDetail = pkgDetails.session_details?.[0];

	const invoiceServices = appointmentInvoice.services || [];
	const consultationServices = consultation.additional_services || [];
	const serviceSource =
		invoiceServices.length > 0 ? invoiceServices : consultationServices;

	let generalServicesTotal = 0;
	let pharmacyServicesTotal = 0;
	serviceSource.forEach((item) => {
		const type = String(item.service_type || "general").toLowerCase();
		const qty = Math.max(1, Number(item.quantity) || 1);
		const lineTotal = toNumber(item.amount ?? toNumber(item.price) * qty);
		if (type === "pharmacy") {
			pharmacyServicesTotal += lineTotal;
		} else if (type !== "laboratory" && type !== "lab" && type !== "treatment_package") {
			generalServicesTotal += lineTotal;
		}
	});

	const billingPharmacyTotal = (record?.billing?.pharmacy_items || []).reduce(
		(sum, item) => sum + toNumber(item.net_total ?? item.amount ?? 0),
		0
	);
	const pharmacyTotal =
		billingPharmacyTotal > 0 ? billingPharmacyTotal : pharmacyServicesTotal;

	return {
		packageName:
			treatmentPackage.package_name ||
			consultation.package_name ||
			appointmentInvoice.package_name ||
			"-",
		packagePrice: toNumber(
			treatmentPackage.total_price ??
				consultation.package_price ??
				appointmentInvoice.package_price
		),
		additionalServicesTotal: generalServicesTotal,
		pharmacyTotal,
		totalPayable: toNumber(payment.net_total ?? appointmentInvoice.net_total),
		totalCollected: toNumber(payment.paid_amount ?? appointmentInvoice.paid_amount),
		remainingBalance: toNumber(payment.due_amount ?? appointmentInvoice.due_amount),
		noOfSessions:
			treatmentPackage.no_of_sessions ??
			consultation.no_of_sessions ??
			appointmentInvoice.no_of_sessions ??
			0,
		currentSession:
			pkgDetails.current_session?.session_name ||
			sessionDetail?.session?.session_name ||
			"-",
		treatmentStartDate:
			firstSession?.session_date_time ||
			treatmentPackage.createdAt ||
			record?.createdAt ||
			"",
		currency: payment.currency || appointmentInvoice.currency || consultation.currency || "INR",
		sessions: buildTreatmentPackageSessionRows(record),
	};
};

const TREATMENT_NON_SERVICE_TYPES = new Set([
	"pharmacy",
	"laboratory",
	"lab",
	"treatment_package",
]);

export const isTreatmentBillableServiceType = (serviceType) =>
	!TREATMENT_NON_SERVICE_TYPES.has(String(serviceType || "general").toLowerCase());

export const isTreatmentPendingService = (item) =>
	String(item?.status || "pending").toLowerCase() === "pending";

export const buildTreatmentAdditionalServices = (record, options = {}) => {
	const {
		billableOnly = false,
		excludePharmacyAndLab = false,
		preferConsultationSource = false,
	} = options;

	const invoiceServices = record?.appointment_invoice?.services || [];
	const consultationServices = record?.consultation?.additional_services || [];
	const addedServices =
		record?.package_payment_details?.treatment_package_payment?.added_services || [];

	let source = [];
	if (preferConsultationSource && consultationServices.length > 0) {
		source = consultationServices;
	} else if (invoiceServices.length > 0) {
		source = invoiceServices;
	} else if (consultationServices.length > 0) {
		source = consultationServices;
	} else if (addedServices.length > 0) {
		source = addedServices;
	}

	const baseCurrency = record?.appointment_invoice?.currency || "INR";

	return source
		.filter((item) => {
			const serviceType = String(item.service_type || "general").toLowerCase();
			if (excludePharmacyAndLab || billableOnly) {
				if (!isTreatmentBillableServiceType(serviceType)) return false;
			}
			if (billableOnly) {
				return isTreatmentPendingService(item);
			}
			return true;
		})
		.map((item, index) => {
			const quantity = Math.max(1, Number(item.quantity) || 1);
			const price = toNumber(item.price);
			const serviceType = String(item.service_type || "general").toLowerCase();
			return {
				id: item._id || item.service_id || `service-${index}`,
				service_id: item.service_id || "",
				service_name: item.service_name || item.name || "-",
				service_type: item.service_type || "general",
				quantity,
				unit_price: price,
				price,
				amount: toNumber(item.amount ?? price * quantity),
				status: item.status || "pending",
				currency: item.currency || baseCurrency,
				isConsultation: serviceType === "consultation",
			};
		});
};

export const buildTreatmentInvestigationItems = (record) => {
	const groups = Array.isArray(record?.investigation) ? record.investigation : [];
	const items = [];

	groups.forEach((group, groupIndex) => {
		(group.examinations || []).forEach((exam, examIndex) => {
			if (exam?.selected === false) return;
			const id = exam._id || `investigation-${groupIndex}-${examIndex}`;
			items.push({
				id,
				test_id: exam._id || exam.investigation_id || id,
				investigation_id: exam._id,
				test_name: exam.name || exam.test_name || "-",
				name: exam.name || exam.test_name || "-",
				type: group.type || "laboratory",
				quantity: 1,
			});
		});
	});

	return items;
};

export const buildTreatmentPackagePaymentHistory = (record) => {
	const pkgDetails = record?.package_payment_details || {};
	const appointmentInvoice = record?.appointment_invoice || {};
	const baseCurrency =
		pkgDetails.payment?.currency || appointmentInvoice.currency || "INR";
	const history =
		pkgDetails.payment_history?.length > 0
			? pkgDetails.payment_history
			: appointmentInvoice.payment_history || [];

	return history
		.filter((item) => toNumber(item?.paid_amount) > 0 || item?.payment_mode)
		.map((item) => ({
			date: item.createdAt || item.payment_date,
			type: formatInvoiceType(item.type || item.payment_type || "payment"),
			description: item.description || item.notes || item.service_name || "-",
			paid_amount: toNumber(item.paid_amount),
			currency: item.collected_currency || item.currency || baseCurrency,
			payment_mode: item.payment_mode || item.payment_status || "-",
		}));
};

export const buildPatientAccountPaymentHistory = (record) => {
	const appointmentInvoice = record?.appointment_invoice || {};
	const baseCurrency =
		appointmentInvoice.currency || record?.billing?.payment?.base_currency || "INR";
	const appointmentHistory = appointmentInvoice.payment_history || [];
	const billingHistory = record?.billing?.payment?.payment_history || [];

	const source =
		appointmentHistory.length > 0
			? appointmentHistory
			: billingHistory.map((item) => ({
					...item,
					payment_mode: item.payment_mode,
					createdAt: item.createdAt,
				}));

	return source
		.filter((item) => toNumber(item?.paid_amount) > 0 || item?.payment_mode)
		.map((item) => {
			const collectedCurrency = item.collected_currency || item.currency || baseCurrency;
			return {
				payment_date: item.createdAt || item.payment_date,
				paid_amount: toNumber(item.paid_amount),
				currency: collectedCurrency,
				base_currency: baseCurrency,
				is_foreign: collectedCurrency !== baseCurrency,
				payment_mode: item.payment_mode || item.payment_status || "-",
				receipt_number: item.receipt_number || "",
			};
		});
};
